import { AlertCircle, AlertTriangle, CheckCircle2, Languages, Loader2 } from 'lucide-react';
import { Badge, type BadgeProps } from '@/components/ui/card';
import { CARD_TYPE_LABELS, sourceLanguageOf, type CardStatus, type CardType, type SourceLanguage, type TranslationStatus } from '@/types/content';

const STATUS_VARIANT: Record<CardStatus, NonNullable<BadgeProps['variant']>> = {
  PUBLISHED: 'success',
  DRAFT: 'warning',
  SCHEDULED: 'info',
  ARCHIVED: 'muted',
};

const STATUS_LABEL: Record<CardStatus, string> = {
  PUBLISHED: 'Published',
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  ARCHIVED: 'Archived',
};

export function StatusBadge({ status, className }: { status: CardStatus; className?: string }) {
  return (
    <Badge variant={STATUS_VARIANT[status] ?? 'muted'} className={className}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
      {STATUS_LABEL[status] ?? status}
    </Badge>
  );
}

export function CardTypeBadge({ cardType, className }: { cardType: CardType; className?: string }) {
  return (
    <Badge variant="outline" className={className}>
      {CARD_TYPE_LABELS[cardType] ?? cardType}
    </Badge>
  );
}

const TRANSLATION_CONFIG: Record<TranslationStatus, { icon: typeof Languages; variant: NonNullable<BadgeProps['variant']>; label: string; spin?: boolean }> = {
  TRANSLATING: { icon: Loader2, variant: 'info', label: 'Translating', spin: true },
  TRANSLATED: { icon: CheckCircle2, variant: 'success', label: 'Translated' },
  PARTIAL: { icon: AlertTriangle, variant: 'warning', label: 'Partial' },
  FAILED: { icon: AlertCircle, variant: 'destructive', label: 'Failed' },
  NONE: { icon: Languages, variant: 'muted', label: 'None' },
};

export function TranslationStatusBadge({ status }: { status: TranslationStatus }) {
  const { icon: Icon, variant, label, spin } = TRANSLATION_CONFIG[status] ?? TRANSLATION_CONFIG.NONE;
  return (
    <Badge variant={variant}>
      <Icon className={spin ? 'animate-spin' : undefined} aria-hidden />
      {label}
    </Badge>
  );
}

/** Shown only for a card written in Croatian (VAP-827); English is the default and gets no badge. */
export function SourceLanguageBadge({ card, className }: { card: { sourceLanguage?: SourceLanguage }; className?: string }) {
  if (sourceLanguageOf(card) !== 'hr') return null;
  return (
    <Badge variant="outline" className={className} title="Written in Croatian. English and the other languages are translated from it.">
      HR
    </Badge>
  );
}
